import { useCallback, useEffect, useState } from 'react'
import { APIBASE } from '../constants/api.constants'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { ShopProductDto, ShopVariantDto } from '../models/shop.model'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../utils/api/server-unavailable'

/**
 * useShopProduct — the storefront product page viewmodel (MVVM).
 *
 * Fetches the PUBLIC `/shop/product[/:id]` (anonymous — no auth context)
 * and tracks the selected variant. Default selection is the first
 * in-stock variant, falling back to the first variant so the page
 * always shows a price/stock state.
 *
 * `productId === null` keeps the legacy single-product behaviour
 * (earliest sellable product); passing an id fetches that specific
 * product for the multi-product detail page (/shop/:id).
 */
export interface UseShopProduct {
  readonly product: ShopProductDto | null
  readonly selectedVariant: ShopVariantDto | null
  readonly selectedVariantId: string | null
  readonly isLoading: boolean
  readonly error: string | null
  readonly selectVariant: (variantId: string) => void
  readonly reload: () => void
}

export function useShopProduct(productId: string | null = null): UseShopProduct {
  const [product, setProduct] = useState<ShopProductDto | null>(null)
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      const url =
        productId === null
          ? `${APIBASE}${APIROUTES.SHOPPRODUCT}`
          : `${APIBASE}${fillPath(APIROUTES.SHOPPRODUCTITEM, { id: productId })}`
      let res: Response
      try {
        res = await fetch(url)
      } catch (err) {
        if (isNetworkError(err)) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
        throw err
      }
      if (res.status >= 500 || !res.ok) {
        throw new Error(SERVER_UNAVAILABLE_MESSAGE)
      }
      const parsed = (await res.json()) as {
        success: boolean
        data?: { product: ShopProductDto | null }
      }
      const p = parsed.data?.product ?? null
      setProduct(p)
      const preferred = p?.variants.find((v) => v.in_stock) ?? p?.variants[0] ?? null
      setSelectedVariantId(preferred?.variant_id ?? null)
    } catch (e) {
      setError(e instanceof Error ? e.message : SERVER_UNAVAILABLE_MESSAGE)
      setProduct(null)
      setSelectedVariantId(null)
    } finally {
      setIsLoading(false)
    }
  }, [productId])

  useEffect(() => {
    void load()
  }, [load])

  const selectVariant = useCallback((variantId: string): void => {
    setSelectedVariantId(variantId)
  }, [])

  const selectedVariant =
    product?.variants.find((v) => v.variant_id === selectedVariantId) ?? null

  return {
    product,
    selectedVariant,
    selectedVariantId,
    isLoading,
    error,
    selectVariant,
    reload: () => {
      void load()
    }
  }
}
